import { useNavigate } from "react-router-dom";
import { BookOpen, Clock, CheckCircle2, AlertTriangle, Circle, XCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import type { ModuleAssignment } from "../lib/api";

interface SchoolStudentArenaProps {
  modules: ModuleAssignment[];
  loading: boolean;
}

type DisplayStatus =
  | "not_started"
  | "in_progress"
  | "completed"
  | "missed"
  | "at_risk"
  | "on_track";

function computeDisplayStatus(mod: ModuleAssignment): DisplayStatus {
  const { status, practice_days_completed, days_remaining } = mod.progress;

  if (status === "completed") return "completed";
  if (status === "missed") return "missed";
  if (status === "not_started") return "not_started";

  // in_progress — check for at_risk
  if (status === "in_progress") {
    const halfWindow = mod.window_days * 0.5;
    const halfPractice = mod.practice_days_required * 0.5;
    if (
      days_remaining !== null &&
      days_remaining < halfWindow &&
      practice_days_completed < halfPractice
    ) {
      return "at_risk";
    }
    return "on_track";
  }

  return "not_started";
}

function statusConfig(status: DisplayStatus) {
  switch (status) {
    case "not_started":
      return {
        label: "Not Started",
        className: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
        icon: Circle,
      };
    case "on_track":
      return {
        label: "On Track",
        className: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
        icon: CheckCircle2,
      };
    case "at_risk":
      return {
        label: "At Risk",
        className: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300",
        icon: AlertTriangle,
      };
    case "completed":
      return {
        label: "Completed",
        className: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
        icon: CheckCircle2,
      };
    case "missed":
      return {
        label: "Missed",
        className: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
        icon: XCircle,
      };
    default:
      return {
        label: "Unknown",
        className: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
        icon: Circle,
      };
  }
}

/** Group modules by classroom_name */
function groupByClassroom(
  modules: ModuleAssignment[],
): Record<string, ModuleAssignment[]> {
  return modules.reduce<Record<string, ModuleAssignment[]>>((acc, mod) => {
    const key = mod.classroom_name;
    if (!acc[key]) acc[key] = [];
    acc[key].push(mod);
    return acc;
  }, {});
}

export default function SchoolStudentArena({
  modules,
  loading,
}: SchoolStudentArenaProps) {
  const navigate = useNavigate();

  // Loading state
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="h-52 bg-gray-200 dark:bg-gray-800 rounded-lg animate-pulse"
          />
        ))}
      </div>
    );
  }

  // Empty state
  if (modules.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <BookOpen className="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4" />
        <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          No Modules Assigned Yet
        </h3>
        <p className="text-gray-500 dark:text-gray-400 max-w-md">
          Your teacher hasn't assigned any modules yet. Check back soon!
        </p>
      </div>
    );
  }

  // All completed state
  const allCompleted = modules.every(
    (m) => m.progress.status === "completed",
  );
  if (allCompleted) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <CheckCircle2 className="w-16 h-16 text-green-500 dark:text-green-400 mb-4" />
        <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          All Caught Up!
        </h3>
        <p className="text-gray-500 dark:text-gray-400 max-w-md">
          You have completed all your assigned modules. Check back soon for new
          assignments.
        </p>
      </div>
    );
  }

  const grouped = groupByClassroom(modules);

  return (
    <div className="space-y-10">
      {Object.entries(grouped).map(([classroomName, classModules]) => (
        <div key={classroomName}>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {classroomName}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {classModules.map((mod) => {
              const displayStatus = computeDisplayStatus(mod);
              const config = statusConfig(displayStatus);
              const StatusIcon = config.icon;
              const progressPercent =
                mod.practice_days_required > 0
                  ? Math.round(
                      (mod.progress.practice_days_completed /
                        mod.practice_days_required) *
                        100,
                    )
                  : 0;

              return (
                <Card
                  key={mod.assignment_id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() =>
                    navigate(`/arena?uuid=${mod.module_id}`)
                  }
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mb-1">
                          {mod.roadmap_name}
                        </p>
                        <CardTitle className="text-base leading-snug">
                          {mod.module_name}
                        </CardTitle>
                      </div>
                      <Badge className={`shrink-0 ${config.className}`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {config.label}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Practice progress */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                        <span>Practice</span>
                        <span>
                          {mod.progress.practice_days_completed}/
                          {mod.practice_days_required} days
                        </span>
                      </div>
                      <Progress value={progressPercent} />
                    </div>

                    {/* Window / days remaining */}
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <Clock className="w-4 h-4 mr-1.5 shrink-0" />
                      {mod.progress.days_remaining !== null ? (
                        <span>
                          {mod.progress.days_remaining} day
                          {mod.progress.days_remaining !== 1 ? "s" : ""}{" "}
                          remaining
                        </span>
                      ) : (
                        <span>Not started</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
